import {  MarkerType } from "@xyflow/react";

/* local functions */
function extractId(token) {
  if (!token) return token;
  let t = token.trim();
  t = t.replace(/^\(+/, "").replace(/\)+$/, "");
  t = t.replace(/^\[+/, "").replace(/\]+$/, "");
  t = t.replace(/^"+|"+$/g, "");
  const m = t.match(/^[a-zA-Z0-9_\-.:]+/);
  return (m && m[0]) || t;
}

function nodeStyleRect(palette, highlighted = false) {
  return {
    width: 260,
    height: 80,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: `1px solid ${palette.nodeBorder}`,
    background: palette.nodeBg,
    background: highlighted ? "#b0b0f7ff" : palette.nodeBg,
    boxShadow: "0 6px 16px rgba(35, 37, 52, 0.06)",
    padding: "8px 12px",
    textAlign: "center",
    color: palette.nodeText,
    overflow: "hidden",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    overflowWrap: "break-word",
    cursor: 'pointer'
  };
}

/* export functions */
export function parseMermaid(mermaidDiagram) {
  if (!mermaidDiagram) return { groups: {}, nodes: {}, edges: [] };

  const lines = mermaidDiagram
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length && /^(graph|flowchart)\b/i.test(lines[0])) lines.shift();

  const groups = {}; // id -> { id, label, children: Set }
  const nodes = {}; // id -> { id, label, rawLabel, groupId }
  const edges = []; // { source, target, arrowType }

  let currentGroup = null;
  const subgraphStart = /^subgraph\s+([^\s\[]+)(?:\s*\[([^\]]+)])?/i;
  const subgraphEnd = /^end$/i;
  const arrowVariants = ["-.->", "==>", "-->", "---"];

  function ensureGroup(id, label) {
    if (!groups[id])
      groups[id] = { id, label: label || id, children: new Set() };
    return groups[id];
  }

  function ensureNode(id, rawLabel) {
    if (!nodes[id])
      nodes[id] = { id, label: id, rawLabel: rawLabel || id, groupId: null };
    if (rawLabel) {
      const title = (rawLabel || "").split(/\n/)[0];
      nodes[id].label = title || nodes[id].label;
      nodes[id].rawLabel = rawLabel;
    }
    return nodes[id];
  }

  for (let rawLine of lines) {
    if (!rawLine) continue;

    // subgraph start
    const sg = rawLine.match(subgraphStart);
    if (sg) {
      const id = sg[1].trim();
      const label = (sg[2] || id).trim();
      ensureGroup(id, label);
      currentGroup = id;
      continue;
    }

    // subgraph end
    if (subgraphEnd.test(rawLine)) {
      currentGroup = null;
      continue;
    }

    // detect edge lines
    let matchedArrow = null;
    let left = null;
    let rightAndRest = null;
    for (const av of arrowVariants) {
      if (rawLine.includes(av)) {
        matchedArrow = av;
        const parts = rawLine.split(av);
        left = parts[0].trim();
        rightAndRest = parts.slice(1).join(av).trim();
        break;
      }
    }

    if (matchedArrow) {
      const sourceId = extractId(left.split(/[\[\(]/)[0]);
      let targetToken = rightAndRest;
      const bracketIdx = targetToken.indexOf("[");

      // capture bracket content if present (label/description)
      let bracketContent = null;
      if (bracketIdx !== -1) {
        const lastBracket = targetToken.lastIndexOf("]");
        if (lastBracket > bracketIdx) {
          bracketContent = targetToken.slice(bracketIdx + 1, lastBracket);
        }
        targetToken = targetToken.slice(0, bracketIdx).trim();
      }

      const targetId = extractId(targetToken.split(/[\[\(]/)[0]);

      // ensure node references exist
      if (sourceId && !nodes[sourceId])
        nodes[sourceId] = {
          id: sourceId,
          label: sourceId,
          rawLabel: sourceId,
          groupId: null,
        };
      if (targetId && !nodes[targetId])
        nodes[targetId] = {
          id: targetId,
          label: targetId,
          rawLabel: targetId,
          groupId: null,
        };

      // If we have bracketContent, update node label/rawLabel
      if (bracketContent && targetId) {
        const title = (bracketContent || "").split(/\n/)[0];
        nodes[targetId].label = title || nodes[targetId].label || targetId;
        nodes[targetId].rawLabel = bracketContent;
      }

      if (currentGroup) {
        if (sourceId && !nodes[sourceId].groupId) {
          nodes[sourceId].groupId = currentGroup;
          if (!groups[currentGroup])
            groups[currentGroup] = {
              id: currentGroup,
              label: currentGroup,
              children: new Set(),
            };
          groups[currentGroup].children.add(sourceId);
        }
        if (targetId && !nodes[targetId].groupId) {
          nodes[targetId].groupId = currentGroup;
          if (!groups[currentGroup])
            groups[currentGroup] = {
              id: currentGroup,
              label: currentGroup,
              children: new Set(),
            };
          groups[currentGroup].children.add(targetId);
        }
      }

      edges.push({
        source: sourceId,
        target: targetId,
        arrowType: matchedArrow,
      });

      continue;
    }

    // node-only declaration line
    const nodeMatch = rawLine.match(/^([^\s\(\[]+)(?:[\(\[](.+)[\)\]])?$/);
    if (nodeMatch) {
      const idToken = nodeMatch[1];
      const content = nodeMatch[2];
      const id = extractId(idToken);

      ensureNode(id, content || id);

      // Only assign group membership when the node is explicitly declared inside a subgraph
      if (currentGroup) {
        // preserve the first declaration: do not overwrite existing groupId
        if (!nodes[id].groupId) {
          nodes[id].groupId = currentGroup;
          ensureGroup(
            currentGroup,
            groups[currentGroup]?.label || currentGroup
          );
          groups[currentGroup].children.add(id);
        } else {
          // node already declared in another group — keep first and ignore further declarations
          // keeping ${nodes[id].groupId}, ignoring ${currentGroup}`);
        }
      }
      continue;
    }
  }

  // finalize groups: convert children sets to arrays
  for (const g of Object.values(groups)) {
    g.children = Array.from(g.children);
  }

  // ensure nodes referenced by edges exist
  edges.forEach((e) => {
    if (e.source && !nodes[e.source])
      nodes[e.source] = {
        id: e.source,
        label: e.source,
        rawLabel: e.source,
        groupId: null,
      };
    if (e.target && !nodes[e.target])
      nodes[e.target] = {
        id: e.target,
        label: e.target,
        rawLabel: e.target,
        groupId: null,
      };
  });

  return { groups, nodes, edges };
}

export function buildFlowForCollapsedView(
  parsed,
  summaries,
  palette,
  expandedGroups,
  selectedNode
) {
  const { groups = {}, nodes: rawNodes = {}, edges: rawEdges = [] } = parsed || {};

  const flowNodes = [];
  const flowEdges = [];

  // Layout constants (tweak to taste)
  const NODE_W = 300;
  const NODE_H = 88;
  const H_SPACING = 40;
  const V_SPACING = 48;
  const GROUP_PADDING_X = 18;
  const GROUP_PADDING_Y = 12;
  const GROUP_HEADER_H = 54;
  const GAP_BETWEEN_GROUPS = 100;
  const MIN_GROUP_W = 320;

  // Build global children/parents maps (used to restrict edges per-group)
  const nodeIds = Object.keys(rawNodes || {});
  const globalChildren = {};
  const globalParents = {};
  nodeIds.forEach((id) => {
    globalChildren[id] = [];
    globalParents[id] = [];
  });
  (rawEdges || []).forEach((e) => {
    if (!e.source || !e.target) return;
    if (globalChildren[e.source] !== undefined) globalChildren[e.source].push(e.target);
    if (globalParents[e.target] !== undefined) globalParents[e.target].push(e.source);
  });

  // Layout routine for a group's nodes — returns local posMap and local bbox
  function layoutGroupNodes(groupNodeIds) {
    // build children map restricted to the group set
    const childrenMap = {};
    groupNodeIds.forEach((nid) => (childrenMap[nid] = []));
    (rawEdges || []).forEach((e) => {
      if (childrenMap[e.source] !== undefined && childrenMap[e.target] !== undefined) {
        childrenMap[e.source].push(e.target);
      }
    });

    // per-group width cache
    const widthCache = {};
    function computeWidth(nid) {
      if (widthCache[nid] !== undefined) return widthCache[nid];
      const kids = (childrenMap[nid] || []).filter(Boolean);
      if (!kids.length) {
        widthCache[nid] = NODE_W;
        return NODE_W;
      }
      const sumKids = kids.reduce((s, k) => s + computeWidth(k), 0);
      const w = Math.max(NODE_W, sumKids + H_SPACING * Math.max(0, kids.length - 1));
      widthCache[nid] = w;
      return w;
    }

    // find roots inside the group (no parents inside)
    const localParents = {};
    groupNodeIds.forEach((nid) => (localParents[nid] = []));
    (rawEdges || []).forEach((e) => {
      if (localParents[e.target] !== undefined && localParents[e.source] !== undefined) {
        localParents[e.target].push(e.source);
      }
    });
    const roots = groupNodeIds.filter((nid) => !localParents[nid] || localParents[nid].length === 0);
    const effectiveRoots = roots.length ? roots : (groupNodeIds.length ? [groupNodeIds[0]] : []);

    // posMap in local group coordinates (top-left origin = 0,0 for the group interior)
    const posMap = {};

    // recursive placement: returns bbox and centerX (all local coords)
    function placeSubtree(nid, leftX, topY) {
      const kids = (childrenMap[nid] || []).filter(Boolean);
      if (!kids.length) {
        const subtreeW = computeWidth(nid);
        const x = leftX + (subtreeW - NODE_W) / 2;
        const y = topY;
        posMap[nid] = { x: Math.round(x), y: Math.round(y) };
        return { minX: x, maxX: x + NODE_W, minY: y, maxY: y + NODE_H, centerX: x + NODE_W / 2 };
      }

      let curX = leftX;
      const centers = [];
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

      for (const kid of kids) {
        const kidW = computeWidth(kid);
        const res = placeSubtree(kid, curX, topY + NODE_H + V_SPACING);
        centers.push(res.centerX);
        minX = Math.min(minX, res.minX);
        minY = Math.min(minY, res.minY);
        maxX = Math.max(maxX, res.maxX);
        maxY = Math.max(maxY, res.maxY);
        curX += kidW + H_SPACING;
      }

      // parent centered between first and last child centers
      const firstCenter = centers[0];
      const lastCenter = centers[centers.length - 1];
      const parentCenter = (firstCenter + lastCenter) / 2;
      const px = parentCenter - NODE_W / 2;
      const py = topY;
      posMap[nid] = { x: Math.round(px), y: Math.round(py) };

      // include parent in bbox
      minX = Math.min(minX, px);
      minY = Math.min(minY, py);
      maxX = Math.max(maxX, px + NODE_W);
      maxY = Math.max(maxY, py + NODE_H);

      return { minX, maxX, minY, maxY, centerX: parentCenter };
    }

    // place each root left→right, advancing cursor by subtree width and/or bbox
    let curLeft = 0;
    let overallMinX = Infinity, overallMinY = Infinity, overallMaxX = -Infinity, overallMaxY = -Infinity;
    for (const r of effectiveRoots) {
      const w = computeWidth(r);
      const placed = placeSubtree(r, curLeft, 0); // local y origin 0 for group interior
      // advance cursor to avoid overlap (safety use both approaches)
      curLeft = Math.max(curLeft + w + H_SPACING, Math.round(placed.maxX + H_SPACING));
      overallMinX = Math.min(overallMinX, placed.minX);
      overallMinY = Math.min(overallMinY, placed.minY);
      overallMaxX = Math.max(overallMaxX, placed.maxX);
      overallMaxY = Math.max(overallMaxY, placed.maxY);
    }

    if (!isFinite(overallMinX)) {
      // empty group fallback
      overallMinX = 0;
      overallMinY = 0;
      overallMaxX = NODE_W;
      overallMaxY = NODE_H;
    }

    // intra-group overlap pass: nudge overlapping siblings right
    const placedNodes = Object.entries(posMap).map(([id, p]) => ({ id, x: p.x, y: p.y }));
    placedNodes.sort((a, b) => a.y - b.y || a.x - b.x);
    for (let i = 0; i < placedNodes.length; i++) {
      for (let j = i + 1; j < placedNodes.length; j++) {
        const A = placedNodes[i], B = placedNodes[j];
        if (Math.abs(A.y - B.y) > NODE_H / 2) continue;
        if (!(A.x + NODE_W + 6 <= B.x || B.x + NODE_W + 6 <= A.x)) {
          // nudge B to right
          const newX = A.x + NODE_W + H_SPACING;
          posMap[B.id].x = newX;
          overallMaxX = Math.max(overallMaxX, newX + NODE_W);
        }
      }
    }

    return { posMap, bbox: { minX: overallMinX, minY: overallMinY, maxX: overallMaxX, maxY: overallMaxY } };
  } // layoutGroupNodes

  // iterate groups in order, create containers and translate local -> relative positions for children
  let nextGroupX = 0;
  const ioGroupIds = Object.keys(groups || {}).filter((gId) => {
    const label = ((groups[gId] && groups[gId].label) || gId || "").toString().toLowerCase();
    return label.includes("input") || label.includes("output") || (gId || "").toString().toLowerCase().includes("input") || (gId || "").toString().toLowerCase().includes("output");
  });

  for (const gId of Object.keys(groups || {})) {
    const g = groups[gId] || { children: [] };
    const isForceExpanded = ioGroupIds.includes(gId);
    const isExpanded = isForceExpanded || (expandedGroups && expandedGroups.has && expandedGroups.has(gId));

    if (!isExpanded) {
      // collapsed group: render compact header box
      const gx = nextGroupX;
      const gy = 0;
      const gw = Math.max(MIN_GROUP_W, NODE_W + 40);
      const gh = GROUP_HEADER_H + NODE_H + 2 * GROUP_PADDING_Y;
      flowNodes.push({
        id: `group__${gId}`,
        type: "customNode",
        data: { label: g.label || gId, isGroup: true, groupId: gId, summary: (summaries || []).find((s) => s.function_name === gId) },
        position: { x: gx, y: gy },
        style: { width: gw, height: gh, borderRadius: 8, background: palette.nodeBg, border: `1px solid ${palette.accent}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" },
      });
      nextGroupX += gw + GAP_BETWEEN_GROUPS;
      continue;
    }

    // expanded group
    const groupNodeIds = (g.children || []).slice();
    if (!groupNodeIds.length) {
      // empty container
      const gx = nextGroupX;
      const gy = 0;
      const gw = Math.max(MIN_GROUP_W, NODE_W + 40);
      const gh = GROUP_HEADER_H + NODE_H + 2 * GROUP_PADDING_Y;
      flowNodes.push({
        id: `group__${gId}`,
        type: "customNode",
        data: { label: g.label || gId, isGroup: true, groupId: gId },
        position: { x: gx, y: gy },
        style: { width: gw, height: gh, borderRadius: 8, background: palette.groupBg, border: `1px solid ${palette.groupBorder}`, padding: 8, cursor: "pointer" },
      });
      nextGroupX += gw + GAP_BETWEEN_GROUPS;
      continue;
    }

    // layout group's internals in local coords
    const { posMap, bbox } = layoutGroupNodes(groupNodeIds);

    // compute group container size and absolute placement (group top-left at gx,gy)
    const padX = GROUP_PADDING_X;
    const padY = GROUP_PADDING_Y;
    const minXBox = Math.floor(bbox.minX - padX);
    const maxXBox = Math.ceil(bbox.maxX + padX);
    const minYBox = Math.floor(bbox.minY - padY);
    const maxYBox = Math.ceil(bbox.maxY + padY);
    const gw = Math.max(MIN_GROUP_W, maxXBox - minXBox);
    const gh = (maxYBox - minYBox) + GROUP_HEADER_H;
    const gx = nextGroupX;
    const gy = 0;

    // add group container node (absolute)
    flowNodes.push({
      id: `group__${gId}`,
      type: "customNode",
      data: { label: g.label || gId, isGroup: true, groupId: gId, summary: (summaries || []).find((s) => s.function_name === gId) },
      position: { x: gx, y: gy },
      style: { width: gw, height: gh, borderRadius: 8, background: palette.groupBg, border: `1px solid ${palette.groupBorder}`, padding: 8, boxSizing: "border-box" },
    });

    // translate local positions into POSITIONS RELATIVE TO THE GROUP (required by React Flow)
    // localMinX should map to gx + padX inside the group
    const localMinX = Math.floor(bbox.minX);
    const localMinY = Math.floor(bbox.minY);
    const offsetX = padX - localMinX; // when added to local.x gives relX inside group
    const offsetY = GROUP_HEADER_H + padY - localMinY; // top offset inside group content area

    for (const nid of groupNodeIds) {
      const local = posMap[nid];
      if (!local) continue;
      const relX = Math.round(local.x + offsetX);
      const relY = Math.round(local.y + offsetY);

      flowNodes.push({
        id: nid,
        type: "customNode",
        data: { label: rawNodes[nid]?.label || nid, summary: (summaries || []).find((s) => s.function_name === nid) },
        // IMPORTANT: position is RELATIVE to group's top-left when parentId is set
        position: { x: relX, y: relY },
        parentId: `group__${gId}`,
        extent: "parent",
        style: nodeStyleRect(palette, selectedNode?.id === nid),
      });
    }

    // advance nextGroupX
    nextGroupX = gx + gw + GAP_BETWEEN_GROUPS;
  } // end group loop

  // Place nodes not in groups (top-level) — simple forest layout anchored at nextGroupX
  const placedIds = new Set(flowNodes.map((n) => n.id));
  const topLevelIds = nodeIds.filter((nid) => !Object.values(groups || {}).some((g) => (g.children || []).includes(nid)));
  if (topLevelIds.length) {
    // build children map restricted to top-level ids
    const childrenMap = {};
    topLevelIds.forEach((nid) => (childrenMap[nid] = []));
    (rawEdges || []).forEach((e) => {
      if (childrenMap[e.source] !== undefined && childrenMap[e.target] !== undefined) {
        childrenMap[e.source].push(e.target);
      }
    });

    // per-top-level width cache & placement similar to groups
    const widthCache = {};
    function computeWidthTop(nid) {
      if (widthCache[nid] !== undefined) return widthCache[nid];
      const kids = (childrenMap[nid] || []).filter(Boolean);
      if (!kids.length) {
        widthCache[nid] = NODE_W;
        return NODE_W;
      }
      const sumKids = kids.reduce((s, k) => s + computeWidthTop(k), 0);
      const w = Math.max(NODE_W, sumKids + H_SPACING * Math.max(0, kids.length - 1));
      widthCache[nid] = w;
      return w;
    }

    const posTop = {};
    function placeTop(nid, leftX, topY) {
      const kids = (childrenMap[nid] || []).filter(Boolean);
      if (!kids.length) {
        const subtreeW = computeWidthTop(nid);
        const x = leftX + (subtreeW - NODE_W) / 2;
        const y = topY;
        posTop[nid] = { x: Math.round(x), y: Math.round(y) };
        return { minX: x, maxX: x + NODE_W, minY: y, maxY: y + NODE_H, centerX: x + NODE_W / 2 };
      }
      let cur = leftX;
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      const centers = [];
      for (const kid of kids) {
        const w = computeWidthTop(kid);
        const r = placeTop(kid, cur, topY + NODE_H + V_SPACING);
        centers.push(r.centerX);
        minX = Math.min(minX, r.minX);
        minY = Math.min(minY, r.minY);
        maxX = Math.max(maxX, r.maxX);
        maxY = Math.max(maxY, r.maxY);
        cur += w + H_SPACING;
      }
      const parentCenter = (centers[0] + centers[centers.length - 1]) / 2;
      const px = parentCenter - NODE_W / 2;
      posTop[nid] = { x: Math.round(px), y: Math.round(topY) };
      minX = Math.min(minX, px);
      maxX = Math.max(maxX, px + NODE_W);
      minY = Math.min(minY, topY);
      maxY = Math.max(maxY, topY + NODE_H);
      return { minX, maxX, minY, maxY, centerX: parentCenter };
    }

    // find roots among top-level ids
    const parentLocal = {};
    topLevelIds.forEach((nid) => (parentLocal[nid] = []));
    (rawEdges || []).forEach((e) => {
      if (parentLocal[e.target] !== undefined && parentLocal[e.source] !== undefined) parentLocal[e.target].push(e.source);
    });
    const roots = topLevelIds.filter((nid) => !parentLocal[nid] || parentLocal[nid].length === 0);
    const effectiveRoots = roots.length ? roots : [topLevelIds[0]];

    let curLeft = nextGroupX + GROUP_PADDING_X;
    let overallMinX = Infinity, overallMaxX = -Infinity;
    for (const r of effectiveRoots) {
      const w = computeWidthTop(r);
      const placed = placeTop(r, curLeft, GROUP_HEADER_H + GROUP_PADDING_Y);
      curLeft = Math.max(curLeft + w + H_SPACING, Math.round(placed.maxX + H_SPACING));
      overallMinX = Math.min(overallMinX, placed.minX);
      overallMaxX = Math.max(overallMaxX, placed.maxX);
    }

    // push top-level nodes into flowNodes
    Object.entries(posTop).forEach(([nid, p]) => {
      if (placedIds.has(nid)) return;
      flowNodes.push({
        id: nid,
        type: "customNode",
        data: { label: rawNodes[nid]?.label || nid, summary: (summaries || []).find((s) => s.function_name === nid) },
        position: { x: p.x, y: p.y },
        style: nodeStyleRect(palette, selectedNode?.id === nid),
      });
    });

    if (isFinite(overallMaxX)) nextGroupX = Math.max(nextGroupX, overallMaxX + GAP_BETWEEN_GROUPS);
  } // end top-level nodes

  // Build edges: if node is in a collapsed group, route to group__id instead
  (rawEdges || []).forEach((e, idx) => {
    if (!e.source || !e.target) return;
    let src = e.source, tgt = e.target;
    const sNode = rawNodes[src], tNode = rawNodes[tgt];
    if (sNode?.groupId && !(expandedGroups && expandedGroups.has && expandedGroups.has(sNode.groupId))) src = `group__${sNode.groupId}`;
    if (tNode?.groupId && !(expandedGroups && expandedGroups.has && expandedGroups.has(tNode.groupId))) tgt = `group__${tNode.groupId}`;
    if (!src || !tgt || src === tgt) return;

    flowEdges.push({
      id: `e-${idx}-${src}-${tgt}`,
      source: src,
      target: tgt,
      style: { stroke: "#333", strokeWidth: 1.5 },
      markerEnd: { type: MarkerType.ArrowClosed, width: 14, height: 10, color: "#333" },
      animated: true,
    });
  });

  console.log("flowNodes", flowNodes);
  return { flowNodes, flowEdges };
}

export const RF_STYLE = {
  backgroundColor: "#FBFAFF",
  width: "100%",
  height: "100%",
};

export const DEFAULT_PALETTE = {
  background: "#ffffff",
  panelBg: "#ffffff",
  groupBg:  "#e8f5f0", // "#eff6ff",
  groupBorder: "#80c6ad",
  nodeBg: "whitesmoke", // "#ECECFF",
  nodeBorder: "#9370DB",
  nodeText: "#111827",
  edge: "#333333",
  mutedEdge: "#999999",
  accent: "#4F46E5",
};

export const MD_STYLE = {
  position: "absolute",
  top: 12,
  left: 12,
  zIndex: 1000,
  display: "flex",
  gap: 8,
  alignItems: "center",
};
